/**
 * CashflowDashboard.tsx
 *
 * Cashflow Dashboard — דשבורד תזרים מזומנים
 *
 * Layout:
 *   A) Filter Bar  — bank account selector, period preset, date pickers, export
 *   B) KPI Cards   — current bank balance, period income, period expenses, forecast end-balance
 *   C) Chart Area  — responsive running-balance line chart (recharts) + view toggles
 *
 * Data pipeline (identical to CashflowForecastTable):
 *   bank_transactions + known_actions + recurring_actions_lines
 *   → offsetCalculator (displayAmount) → rolloverUtils (effectiveDate)
 *   → unified ForecastRow[] sorted by effectiveDate ASC, Bank first on ties
 *   → sequential running balance
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { CategorySummarySection } from './CategorySummarySection';
import { ManagementReportPrintView } from './ManagementReportPrintView';
import type { PrintViewProps } from './ManagementReportPrintView';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import {
  CashFlowDrillDownModal,
  type DrillDownRow,
  type DrillDownTarget,
  type DrillDownFilter,
} from './CashFlowDrillDownModal';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Area,
  ComposedChart,
  Bar,
} from 'recharts';
import { Wallet, TrendingUp, TrendingDown, Target, ChevronDown, Download, RefreshCw, CalendarDays, ChartBar as BarChart2, Table2, TriangleAlert as AlertTriangle, Building2, Loader as Loader2, FileSpreadsheet, Printer } from 'lucide-react';
import { useCompany } from '../../context/CompanyContext';
import { bankTransactionsService } from '../../lib/bankTransactionsService';
import { bankAccountsService } from '../../lib/bankAccountsService';
import { knownActionsService } from '../../lib/knownActionsService';
import type { KnownAction } from '../../lib/knownActionsService';
import { recurringActionsService } from '../../lib/recurringActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { buildOffsetMap } from '../../lib/offsetCalculator';
import {
  getTodayString,
  buildUnifiedRows,
} from '../../lib/rolloverUtils';
import type {
  BankTransaction,
  RecurringActionLine,
  OffsetCode,
  BankAccount,
} from '../../types/database';

// ─── Currency formatter ───────────────────────────────────────────────────────

const ILS = new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const ILS2 = new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// ─── Period presets ───────────────────────────────────────────────────────────

type PeriodPreset =
  | 'this_month'
  | 'prev_month'
  | 'next_month'
  | 'this_quarter'
  | 'prev_quarter'
  | 'next_3_months'
  | 'next_6_months'
  | 'next_year'
  | 'this_year'
  | 'prev_year';

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  prev_month:    'חודש קודם',
  this_month:    'החודש הנוכחי',
  next_month:    'החודש הבא',
  prev_quarter:  'רבעון קודם',
  this_quarter:  'הרבעון הנוכחי',
  next_3_months: '3 חודשים קדימה',
  next_6_months: '6 חודשים קדימה',
  next_year:     'השנה הבאה',
  prev_year:     'שנה קודמת',
  this_year:     'השנה הנוכחית',
};

function getPeriodDates(preset: PeriodPreset): { start: string; end: string } {
  const now   = new Date();
  const y     = now.getFullYear();
  const m     = now.getMonth(); // 0-based

  // Use local date components to avoid the UTC off-by-one bug in UTC+2/+3 (Israel):
  // new Date(year, month, day).toISOString() emits UTC midnight which is the PREVIOUS
  // local day when the browser is in a positive-offset timezone.
  const localISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const firstOfMonth = (year: number, month: number) =>
    new Date(year, month, 1);
  const lastOfMonth = (year: number, month: number) =>
    new Date(year, month + 1, 0);

  switch (preset) {
    // ── Past ──────────────────────────────────────────────────────────────
    case 'prev_month':
      return { start: localISO(firstOfMonth(y, m - 1)), end: localISO(lastOfMonth(y, m - 1)) };
    case 'prev_quarter': {
      const pqStart = Math.floor(m / 3) * 3 - 3;   // first month of previous quarter
      return { start: localISO(firstOfMonth(y, pqStart)), end: localISO(lastOfMonth(y, pqStart + 2)) };
    }
    case 'prev_year':
      return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
    // ── Present ───────────────────────────────────────────────────────────
    case 'this_month':
      return { start: localISO(firstOfMonth(y, m)), end: localISO(lastOfMonth(y, m)) };
    case 'this_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { start: localISO(firstOfMonth(y, qStart)), end: localISO(lastOfMonth(y, qStart + 2)) };
    }
    case 'this_year':
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    // ── Future ────────────────────────────────────────────────────────────
    case 'next_month':
      return { start: localISO(firstOfMonth(y, m + 1)), end: localISO(lastOfMonth(y, m + 1)) };
    case 'next_3_months': {
      const end3 = new Date(now);
      end3.setMonth(end3.getMonth() + 3);
      end3.setDate(0); // last day of 3rd month ahead
      return { start: localISO(firstOfMonth(y, m)), end: localISO(end3) };
    }
    case 'next_6_months': {
      const end6 = new Date(now);
      end6.setMonth(end6.getMonth() + 6);
      end6.setDate(0);
      return { start: localISO(firstOfMonth(y, m)), end: localISO(end6) };
    }
    case 'next_year':
      return { start: `${y + 1}-01-01`, end: `${y + 1}-12-31` };
    default:
      return { start: localISO(firstOfMonth(y, m)), end: localISO(lastOfMonth(y, m)) };
  }
}

// ─── Unified row — carries enough detail for drill-down ─────────────────────

interface CashRow {
  id:            string;
  effectiveDate: string;
  description:   string;
  amount:        number;
  sourceType:    'Bank' | 'Known' | 'Recurring' | 'RolledOver';
  bankAccountId: string | null;
  bankNickname:  string | null;
  categoryName:  string | null;
  status:        string;
}

// ─── Chart data point ─────────────────────────────────────────────────────────

interface ChartPoint {
  date:           string;  // DD/MM
  fullDate:       string;  // YYYY-MM-DD
  balance:        number;
  income:         number;
  expense:        number;
}

// ─── KPI Card component ───────────────────────────────────────────────────────

interface KpiCardProps {
  title:    string;
  value:    string;
  subtitle: string;
  icon:     React.ReactNode;
  accent:   'blue' | 'green' | 'red' | 'amber';
  loading?: boolean;
}

const ACCENT = {
  blue:  { bg: 'bg-blue-50',   border: 'border-blue-200',  icon: 'bg-blue-100',   text: 'text-blue-700',   value: 'text-blue-900'  },
  green: { bg: 'bg-green-50',  border: 'border-green-200', icon: 'bg-green-100',  text: 'text-green-700',  value: 'text-green-900' },
  red:   { bg: 'bg-red-50',    border: 'border-red-200',   icon: 'bg-red-100',    text: 'text-red-700',    value: 'text-red-900'   },
  amber: { bg: 'bg-amber-50',  border: 'border-amber-200', icon: 'bg-amber-100',  text: 'text-amber-700',  value: 'text-amber-900' },
};

function KpiCard({ title, value, subtitle, icon, accent, loading }: KpiCardProps) {
  const a = ACCENT[accent];
  return (
    <div className={`rounded-xl border ${a.border} ${a.bg} p-5 flex flex-col gap-3 shadow-sm`}>
      <div className="flex items-start justify-between">
        <p className={`text-sm font-semibold ${a.text} leading-tight`}>{title}</p>
        <div className={`${a.icon} p-2 rounded-lg`}>{icon}</div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          <span className="text-sm text-gray-400">טוען…</span>
        </div>
      ) : (
        <>
          <p className={`text-2xl font-extrabold tabular-nums ${a.value} leading-none`}>{value}</p>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </>
      )}
    </div>
  );
}

// ─── Custom recharts tooltip ──────────────────────────────────────────────────

/**
 * Rich tooltip shown on chart hover — display-only, NOT clickable.
 * Pointer-events are disabled on the wrapper so the tooltip never
 * intercepts mouse events and the chart's own onClick fires normally.
 *
 * Drill-down is triggered by clicking the chart itself (AreaChart onClick)
 * or the active dot, NOT by clicking inside this tooltip.
 */
interface ChartTooltipProps {
  active?:  boolean;
  payload?: Array<{ value: number; payload: ChartPoint }>;
  label?:   string;
}

function ChartTooltipContent({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const point   = payload[0]?.payload as ChartPoint | undefined;
  const balance = payload[0]?.value   as number ?? 0;
  const income  = point?.income  ?? 0;
  const expense = point?.expense ?? 0;

  return (
    // pointer-events-none: tooltip is purely informational.
    // Clicks pass straight through to the chart underneath.
    <div
      className="pointer-events-none bg-white border border-gray-200 rounded-xl shadow-xl p-0 text-right min-w-[200px] overflow-hidden select-none"
      dir="rtl"
    >
      {/* Header — date label */}
      <div className="bg-gray-50 border-b border-gray-100 px-4 py-2">
        <p className="text-xs font-semibold text-gray-600 tracking-wide">{label}</p>
      </div>

      <div className="px-4 py-3 space-y-2">
        {/* Income row */}
        {income > 0 && (
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-green-700 font-medium">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              סה&quot;כ נכנס
            </span>
            <span className="text-sm font-bold text-green-700 tabular-nums">
              {ILS2.format(income)}
            </span>
          </div>
        )}

        {/* Expense row */}
        {expense < 0 && (
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              סה&quot;כ יוצא
            </span>
            <span className="text-sm font-bold text-red-600 tabular-nums">
              {ILS2.format(expense)}
            </span>
          </div>
        )}

        {/* Placeholder when the bucket has zero activity */}
        {income === 0 && expense === 0 && (
          <p className="text-xs text-gray-400 text-center py-0.5">אין פעילות</p>
        )}

        {/* Separator */}
        <hr className="border-gray-100 my-1" />

        {/* Cumulative balance */}
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-xs text-gray-600 font-semibold">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
            יתרה מצטברת
          </span>
          <span className={`text-sm font-extrabold tabular-nums ${
            balance >= 0 ? 'text-emerald-700' : 'text-red-700'
          }`}>
            {ILS2.format(balance)}
          </span>
        </div>
      </div>

      {/* Footer — click-hint (informational only) */}
      <div className="bg-gray-50 border-t border-gray-100 px-4 py-1.5">
        <p className="text-[10px] text-gray-400 text-center">לחץ לפתיחת פירוט</p>
      </div>
    </div>
  );
}

// ─── Chart-point builder (shared by live chart AND print view) ──────────────

/**
 * Produces the same ChartPoint array used by the Recharts chart.
 * Extracted as a standalone function so it can be called both inside the
 * live `chartData` memo (with the user's current viewMode) AND inside the
 * `printChartData` memo (which always forces 'monthly' when dayCount > 31).
 *
 * @param periodRows     - CashRow[] filtered to the visible period
 * @param filteredByAccount - CashRow[] for ALL dates (used to compute opening balance)
 * @param startDate      - YYYY-MM-DD period start
 * @param endDate        - YYYY-MM-DD period end
 * @param mode           - bucketing granularity
 */
function buildChartPoints(
  periodRows:        CashRow[],
  filteredByAccount: CashRow[],
  startDate:         string,
  endDate:           string,
  mode:              ChartViewMode,
): ChartPoint[] {
  if (periodRows.length === 0) return [];

  const toLocalISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Daily income/expense deltas keyed by YYYY-MM-DD
  const deltaMap = new Map<string, { income: number; expense: number }>();
  for (const row of periodRows) {
    const cur = deltaMap.get(row.effectiveDate) ?? { income: 0, expense: 0 };
    if (row.amount >= 0) cur.income  += row.amount;
    else                  cur.expense += row.amount;
    deltaMap.set(row.effectiveDate, cur);
  }

  // Enumerate every calendar date in the range (noon anchor avoids DST edge cases)
  const dates: string[] = [];
  const stop   = new Date(`${endDate}T12:00:00`);
  const cursor = new Date(`${startDate}T12:00:00`);
  while (cursor <= stop) {
    dates.push(toLocalISO(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  // Bucket dates by granularity
  type Bucket = { key: string; label: string; income: number; expense: number };
  const buckets = new Map<string, Bucket>();
  const monthNames = ['ינו','פבר','מרץ','אפר','מאי','יונ','יול','אוג','ספט','אוק','נוב','דצמ'];

  for (const dateStr of dates) {
    const [y, m, d] = dateStr.split('-');
    let bucketKey: string;
    let label: string;

    if (mode === 'daily') {
      bucketKey = dateStr;
      label     = `${d}/${m}`;
    } else if (mode === 'weekly') {
      const dt        = new Date(`${dateStr}T12:00:00`);
      const weekStart = new Date(dt);
      weekStart.setDate(dt.getDate() - dt.getDay());
      bucketKey       = toLocalISO(weekStart);
      const wp        = bucketKey.split('-');
      label           = `${wp[2] ?? ''}/${wp[1] ?? ''}`;
    } else {
      // monthly
      bucketKey = `${y}-${m}`;
      label     = `${monthNames[parseInt(m ?? '1', 10) - 1] ?? (m ?? '')} ${y}`;
    }

    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, { key: bucketKey, label, income: 0, expense: 0 });
    }
    const b   = buckets.get(bucketKey)!;
    const day = deltaMap.get(dateStr) ?? { income: 0, expense: 0 };
    b.income  += day.income;
    b.expense += day.expense;
  }

  // Compute opening balance = sum of all account-filtered rows BEFORE the period
  const prePeriodBalance = filteredByAccount
    .filter(r => r.effectiveDate < startDate)
    .reduce((s, r) => s + r.amount, 0);

  let running = prePeriodBalance;
  return Array.from(buckets.values())
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(bucket => {
      running += bucket.income + bucket.expense;
      return {
        date:     bucket.label,
        fullDate: bucket.key,
        balance:  Math.round(running * 100) / 100,
        income:   Math.round(bucket.income  * 100) / 100,
        expense:  Math.round(bucket.expense * 100) / 100,
      };
    });
}

// ─── View-toggle button ───────────────────────────────────────────────────────

type ChartViewMode = 'daily' | 'weekly' | 'monthly';
type DisplayMode   = 'chart' | 'table';

interface ToggleBtnProps {
  active:   boolean;
  onClick:  () => void;
  children: React.ReactNode;
}

function ToggleBtn({ active, onClick, children }: ToggleBtnProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
        active
          ? 'bg-primary-600 text-white shadow-sm'
          : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

// Build a lightweight CashRow array from the Bank-is-Truth unified rows.
// We keep this adapter so all KPI / chart logic below works unchanged.
function toCashRows(
  bankTxns:       BankTransaction[],
  knownActions:   KnownAction[],
  recurringLines: RecurringActionLine[],
  offsetMap:      ReturnType<typeof buildOffsetMap>,
): CashRow[] {
  const forecastRows = buildUnifiedRows({ bankTxns, knownActions, recurringLines, offsetMap });
  // Build a fast bank-tx lookup for bankAccountId (not in ForecastRow)
  const bankMap = new Map(bankTxns.map(tx => [tx.id, tx]));
  return forecastRows.map(r => ({
    id:            r.id,
    effectiveDate: r.effectiveDate,
    description:   r.description,
    amount:        r.amount,
    sourceType:    r.sourceType,
    bankAccountId: r.sourceType === 'Bank'
      ? (bankMap.get(r.id)?.bank_account_id ?? null)
      : null,
    bankNickname:  r.bankAccountNickname !== '—' ? r.bankAccountNickname : null,
    categoryName:  r.accountingCategoryName !== '—' ? r.accountingCategoryName : null,
    status:        r.status,
  }));
}

export function CashflowDashboard() {
  const { activeCompanyId } = useCompany();
  const today = getTodayString();

  // ── Raw data ──────────────────────────────────────────────────────────────
  const [bankTxns,       setBankTxns]       = useState<BankTransaction[]>([]);
  const [knownActions,   setKnownActions]   = useState<KnownAction[]>([]);
  const [recurringLines, setRecurringLines] = useState<RecurringActionLine[]>([]);
  const [offsetCodes,    setOffsetCodes]    = useState<OffsetCode[]>([]);
  const [bankAccounts,   setBankAccounts]   = useState<BankAccount[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [errorMsg,       setErrorMsg]       = useState<string | null>(null);
  const [refreshKey,     setRefreshKey]     = useState(0);

  // ── Drill-down state ──────────────────────────────────────────────────────
  const [drillTarget,    setDrillTarget]    = useState<DrillDownTarget | null>(null);

  // ── Filter state ──────────────────────────────────────────────────────────
  const defaultPeriod = getPeriodDates('this_month');
  const [selectedBankId, setSelectedBankId]  = useState<string>('all');
  const [periodPreset,   setPeriodPreset]    = useState<PeriodPreset>('this_month');
  const [startDate,      setStartDate]       = useState(defaultPeriod.start);
  const [endDate,        setEndDate]         = useState(defaultPeriod.end);
  const [viewMode,       setViewMode]        = useState<ChartViewMode>('daily');
  const [displayMode,    setDisplayMode]     = useState<DisplayMode>('chart');

  // ── Export dropdown state ─────────────────────────────────────────────────
  const [exportOpen, setExportOpen] = useState(false);

  // ── Print ref + react-to-print hook ──────────────────────────────────────
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `cashflow-report-${new Date().getFullYear()}`,
    pageStyle: `
      @page { size: landscape; margin: 10mm; }
      @media print {
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `,
  });

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [bankRes, knownRes, recurringRes, offsetRes, accountsRes] = await Promise.all([
        bankTransactionsService.getBankTransactions(activeCompanyId),
        knownActionsService.getKnownActions(activeCompanyId),
        recurringActionsService.getRecurringActions(activeCompanyId),
        offsetCodesService.getOffsetCodes(activeCompanyId),
        bankAccountsService.getBankAccounts(activeCompanyId),
      ]);
      setBankTxns      (bankRes.data       ?? []);
      setKnownActions  (knownRes.data      ?? []);
      setRecurringLines(recurringRes.data  ?? []);
      setOffsetCodes   (offsetRes.data     ?? []);
      setBankAccounts  (accountsRes.data   ?? []);
    } catch (err) {
      console.error('CashflowDashboard load error:', err);
      setErrorMsg('שגיאה בטעינת נתוני התזרים. נסה לרענן.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, refreshKey]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Update dates when preset changes ──────────────────────────────────────
  const handlePresetChange = (preset: PeriodPreset) => {
    setPeriodPreset(preset);
    const { start, end } = getPeriodDates(preset);
    setStartDate(start);
    setEndDate(end);
  };

  // ── Offset map ────────────────────────────────────────────────────────────
  const offsetMap = useMemo(
    () => buildOffsetMap(recurringLines, knownActions, offsetCodes),
    [recurringLines, knownActions, offsetCodes],
  );

  // ── Build unified flat rows (Bank-is-Truth) ─────────────────────────────
  const allRows = useMemo<CashRow[]>(
    () => toCashRows(bankTxns.filter(tx => !tx.is_cc_settlement), knownActions, recurringLines, offsetMap),
    [bankTxns, knownActions, recurringLines, offsetMap],
  );

  // ── Apply bank-account filter ─────────────────────────────────────────────
  const filteredByAccount = useMemo(() => {
    if (selectedBankId === 'all') return allRows;
    return allRows.filter(r => r.bankAccountId === selectedBankId);
  }, [allRows, selectedBankId]);

  // ── KPI 1: Current Balance — sum of ALL bank transactions up to today ─────
  const currentBalance = useMemo(() => {
    return filteredByAccount
      .filter(r => r.sourceType === 'Bank' && r.effectiveDate <= today)
      .reduce((s, r) => s + r.amount, 0);
  }, [filteredByAccount, today]);

  // ── Period rows (inside start–end filter) ─────────────────────────────────
  const periodRows = useMemo(() => {
    return filteredByAccount.filter(
      r => r.effectiveDate >= startDate && r.effectiveDate <= endDate,
    );
  }, [filteredByAccount, startDate, endDate]);

  // ── KPI 2 & 3: Period income / expenses ───────────────────────────────────
  const periodIncome   = useMemo(() =>
    periodRows.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0),
    [periodRows],
  );
  const periodExpenses = useMemo(() =>
    periodRows.filter(r => r.amount < 0).reduce((s, r) => s + r.amount, 0),
    [periodRows],
  );

  // ── KPI 4: Forecasted end-balance — running balance up to endDate ─────────
  const forecastEndBalance = useMemo(() => {
    return filteredByAccount
      .filter(r => r.effectiveDate <= endDate)
      .reduce((s, r) => s + r.amount, 0);
  }, [filteredByAccount, endDate]);

  // ── Chart data — group by day/week/month (uses shared buildChartPoints) ─────
  const chartData = useMemo<ChartPoint[]>(
    () => buildChartPoints(periodRows, filteredByAccount, startDate, endDate, viewMode),
    [periodRows, filteredByAccount, startDate, endDate, viewMode],
  );

  // ── Print chart data — ALWAYS monthly when period > 31 days, else daily ────
  // This is the single source of truth for the PDF Section 2 monthly table.
  // It uses the same buildChartPoints function, so income/expense/balance
  // figures are arithmetically identical to the live chart; only the
  // bucketing mode is forced to match the print layout requirement.
  const printChartData = useMemo<ChartPoint[]>(() => {
    const days = Math.round(
      (Date.parse(endDate + 'T12:00:00Z') - Date.parse(startDate + 'T12:00:00Z')) / 86_400_000,
    ) + 1;
    const printMode: ChartViewMode = days > 31 ? 'monthly' : 'daily';
    return buildChartPoints(periodRows, filteredByAccount, startDate, endDate, printMode);
  }, [periodRows, filteredByAccount, startDate, endDate]);

  // ── Drill-down helper ─────────────────────────────────────────────────────
  /**
   * Compute the bucket date-range for a given ChartPoint bucket key.
   * For daily:   bucketStart = bucketEnd = fullDate (YYYY-MM-DD)
   * For weekly:  bucketStart = fullDate (Sunday start), bucketEnd = Saturday
   * For monthly: fullDate is YYYY-MM; derive first/last day
   *
   * All date arithmetic uses local-date components (not toISOString) to avoid
   * the UTC+2/+3 off-by-one-day bug.
   */
  const toLocalISO = useCallback((d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    [],
  );

  const openDrillDown = useCallback(
    (point: ChartPoint, filter: DrillDownFilter) => {
      let bucketStart: string;
      let bucketEnd:   string;

      if (viewMode === 'daily') {
        bucketStart = point.fullDate;
        bucketEnd   = point.fullDate;
      } else if (viewMode === 'weekly') {
        // fullDate = Sunday start of week (YYYY-MM-DD)
        bucketStart = point.fullDate;
        const end = new Date(`${point.fullDate}T12:00:00`);
        end.setDate(end.getDate() + 6);
        bucketEnd = toLocalISO(end);
      } else {
        // fullDate = YYYY-MM  (monthly bucket key)
        const [y, m] = point.fullDate.split('-').map(Number);
        const first  = new Date(y!, (m ?? 1) - 1, 1, 12, 0, 0);
        const last   = new Date(y!, (m ?? 1), 0, 12, 0, 0);
        bucketStart  = toLocalISO(first);
        bucketEnd    = toLocalISO(last);
      }

      setDrillTarget({
        bucketStart,
        bucketEnd,
        label:  point.date,   // already human-readable (e.g. "ינו 2026")
        filter,
      });
    },
    [viewMode, toLocalISO],
  );

  // ── All rows coerced to DrillDownRow shape ────────────────────────────────
  const drillRows = useMemo<DrillDownRow[]>(
    () =>
      filteredByAccount.map(r => ({
        id:            r.id,
        effectiveDate: r.effectiveDate,
        description:   r.description,
        categoryName:  r.categoryName,
        amount:        r.amount,
        sourceType:    r.sourceType,
        bankNickname:  r.bankNickname,
      })),
    [filteredByAccount],
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmtBig = (n: number) =>
    Math.abs(n) >= 1_000_000
      ? `₪${(n / 1_000_000).toFixed(1)}M`
      : Math.abs(n) >= 1_000
      ? `₪${(n / 1_000).toFixed(0)}K`
      : ILS2.format(n);

  // ── Excel export ─────────────────────────────────────────────────────────
  const handleExcelExport = () => {
    const wsData = [
      ['תאריך', 'הכנסות', 'הוצאות', 'יתרה מצטברת'],
      ...chartData.map(p => [p.date, p.income, p.expense, p.balance]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // Column widths
    ws['!cols'] = [{ wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'תזרים מזומנים');
    XLSX.writeFile(wb, `cashflow-${startDate}-${endDate}.xlsx`);
    setExportOpen(false);
  };

  // ── All-year ForecastRows for the print-view category matrices ───────────
  const { activeCompany } = useCompany();
  const currentYear = new Date().getFullYear();
  const yearStart   = `${currentYear}-01-01`;
  const yearEnd     = `${currentYear}-12-31`;

  const allYearForecastRows = useMemo(
    () =>
      buildUnifiedRows({ bankTxns, knownActions, recurringLines, offsetMap })
        .filter(r => r.effectiveDate >= yearStart && r.effectiveDate <= yearEnd),
    [bankTxns, knownActions, recurringLines, offsetMap, yearStart, yearEnd],
  );

  // ── Print view props ──────────────────────────────────────────────────────
  // printChartData is the single source of truth for the PDF Section 2 table:
  // it is always bucketed at the right resolution and shares the EXACT same
  // arithmetic as the live chart (same buildChartPoints function, same inputs).
  const printProps: PrintViewProps = {
    companyName:  activeCompany?.legal_name ?? 'החברה',
    startDate,
    endDate,
    chartData:    printChartData,   // ← print-specific bucketing (never viewMode-dependent)
    periodRows:   (() => {
      // Detail rows for the ≤31-day granular table in the PDF.
      //
      // SORT FIRST — buildUnifiedRows does not sort; Bank transactions are
      // fetched ORDER BY transaction_date DESC, so without an explicit sort
      // the most-recent Bank rows appear at the top (e.g. 30/04 before 04/04).
      // effectiveDate is always 'YYYY-MM-DD', so localeCompare is chronological.
      // Same-date tie-break: Bank rows first (matches CashflowForecastTable).
      const sorted = [...periodRows].sort((a, b) => {
        const d = a.effectiveDate.localeCompare(b.effectiveDate);
        if (d !== 0) return d;
        return (a.sourceType === 'Bank' ? 0 : 1) - (b.sourceType === 'Bank' ? 0 : 1);
      });

      // runningBalance must be recomputed in the sorted order so the
      // "יתרה מצטברת" column in the PDF is arithmetically correct.
      const opening = filteredByAccount
        .filter(r => r.effectiveDate < startDate)
        .reduce((s, r) => s + r.amount, 0);
      let rb = opening;
      return sorted.map(r => {
        rb += r.amount;
        return {
          effectiveDate:  r.effectiveDate,
          description:    r.description,
          amount:         r.amount,
          runningBalance: Math.round(rb * 100) / 100,
        };
      });
    })(),
    allYearRows:  allYearForecastRows,
    selectedYear: currentYear,
  };

  // ── Guard: no company ─────────────────────────────────────────────────────
  if (!activeCompanyId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <Building2 className="h-12 w-12 mb-4 text-gray-300" />
        <p className="text-sm font-medium">בחר חברה פעילה כדי לצפות בדשבורד התזרים</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" dir="rtl">

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Section A — Filter Bar                                              */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">

          {/* Bank account selector */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs font-semibold text-gray-500 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              חשבון בנק
            </label>
            <div className="relative">
              <select
                value={selectedBankId}
                onChange={e => setSelectedBankId(e.target.value)}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-8 pr-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">כל החשבונות</option>
                {bankAccounts.map(ba => (
                  <option key={ba.id} value={ba.id}>
                    {ba.nickname} – {ba.bank_name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* From date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500">מתאריך</label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPeriodPreset('this_month'); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* To date */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500">עד תאריך</label>
            <input
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPeriodPreset('this_month'); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* ── Period preset dropdown ── */}
          <div className="flex flex-col gap-1 min-w-[170px]">
            <label className="text-xs font-semibold text-gray-500 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />
              קיצור
            </label>
            <div className="relative">
              <select
                value={periodPreset}
                onChange={e => handlePresetChange(e.target.value as PeriodPreset)}
                className="w-full appearance-none rounded-lg border border-gray-300 bg-white
                  pl-8 pr-3 py-2 text-sm text-gray-800
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <optgroup label="עבר">
                  <option value="prev_month">{PERIOD_LABELS.prev_month}</option>
                  <option value="prev_quarter">{PERIOD_LABELS.prev_quarter}</option>
                  <option value="prev_year">{PERIOD_LABELS.prev_year}</option>
                </optgroup>
                <optgroup label="הווה">
                  <option value="this_month">{PERIOD_LABELS.this_month}</option>
                  <option value="this_quarter">{PERIOD_LABELS.this_quarter}</option>
                  <option value="this_year">{PERIOD_LABELS.this_year}</option>
                </optgroup>
                <optgroup label="עתיד">
                  <option value="next_month">{PERIOD_LABELS.next_month}</option>
                  <option value="next_3_months">{PERIOD_LABELS.next_3_months}</option>
                  <option value="next_6_months">{PERIOD_LABELS.next_6_months}</option>
                  <option value="next_year">{PERIOD_LABELS.next_year}</option>
                </optgroup>
              </select>
              <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Refresh */}
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            title="רענן נתונים"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary-500' : ''}`} />
            <span className="hidden sm:inline">רענן</span>
          </button>

          {/* Export dropdown */}
          <div className="relative">
            <button
              onClick={() => setExportOpen(o => !o)}
              title="ייצוא"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary-300 bg-primary-50 text-sm text-primary-700 hover:bg-primary-100 transition-colors font-semibold"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">ייצוא</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-70" />
            </button>

            {exportOpen && (
              <>
                {/* Backdrop — click outside to close */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setExportOpen(false)}
                />
                {/* Dropdown panel */}
                <div className="absolute left-0 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-xl z-20 overflow-hidden"
                     dir="rtl">
                  <button
                    onClick={handleExcelExport}
                    className="flex w-full items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-emerald-50 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div className="text-right">
                      <p className="font-semibold text-emerald-700">ייצוא לאקסל</p>
                      <p className="text-[11px] text-gray-400">קובץ .xlsx עם נתוני התזרים</p>
                    </div>
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button
                    onClick={() => { handlePrint(); setExportOpen(false); }}
                    className="flex w-full items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                  >
                    <Printer className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="text-right">
                      <p className="font-semibold text-blue-700">ייצוא ל-PDF</p>
                      <p className="text-[11px] text-gray-400">דוח ניהולי — גרף + טבלאות</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Error banner */}
        {errorMsg && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {errorMsg}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Section B — KPI Cards                                               */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

        {/* Card 1 — Current Balance */}
        <KpiCard
          title="יתרה בבנק עכשיו"
          value={fmtBig(currentBalance)}
          subtitle={`תנועות בנק עד היום – ${today.split('-').reverse().join('/')}`}
          icon={<Wallet className="w-5 h-5 text-amber-600" />}
          accent="amber"
          loading={loading}
        />

        {/* Card 2 — Period Income */}
        <KpiCard
          title="הכנסות בתקופה"
          value={fmtBig(periodIncome)}
          subtitle={`${startDate.split('-').reverse().join('/')} – ${endDate.split('-').reverse().join('/')}`}
          icon={<TrendingUp className="w-5 h-5 text-green-600" />}
          accent="green"
          loading={loading}
        />

        {/* Card 3 — Period Expenses */}
        <KpiCard
          title="הוצאות בתקופה"
          value={fmtBig(periodExpenses)}
          subtitle={`${periodRows.filter(r => r.amount < 0).length} פעולות הוצאה`}
          icon={<TrendingDown className="w-5 h-5 text-red-600" />}
          accent="red"
          loading={loading}
        />

        {/* Card 4 — Forecast End Balance */}
        <KpiCard
          title="תחזית יתרה לסוף התקופה"
          value={fmtBig(forecastEndBalance)}
          subtitle={`צפוי ל-${endDate.split('-').reverse().join('/')}`}
          icon={<Target className="w-5 h-5 text-blue-600" />}
          accent={forecastEndBalance >= 0 ? 'blue' : 'red'}
          loading={loading}
        />
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Section C — Chart Area                                              */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">

        {/* Chart header row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              יתרה מצטברת לאורך זמן
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {`${startDate.split('-').reverse().join('/')} – ${endDate.split('-').reverse().join('/')}`}
              &nbsp;·&nbsp;{chartData.length} נקודות נתון
            </p>
          </div>

          {/* Toggle buttons */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Granularity toggles */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <ToggleBtn active={viewMode === 'daily'}   onClick={() => setViewMode('daily')}>יומי</ToggleBtn>
              <ToggleBtn active={viewMode === 'weekly'}  onClick={() => setViewMode('weekly')}>שבועי</ToggleBtn>
              <ToggleBtn active={viewMode === 'monthly'} onClick={() => setViewMode('monthly')}>חודשי</ToggleBtn>
            </div>

            {/* Chart/Table display toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <ToggleBtn active={displayMode === 'chart'} onClick={() => setDisplayMode('chart')}>
                <span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5" />גרף</span>
              </ToggleBtn>
              <ToggleBtn active={displayMode === 'table'} onClick={() => setDisplayMode('table')}>
                <span className="flex items-center gap-1"><Table2 className="w-3.5 h-3.5" />טבלה</span>
              </ToggleBtn>
            </div>
          </div>
        </div>

        {/* Loading / empty / chart */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <BarChart2 className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm font-medium">אין נתונים לתקופה הנבחרת</p>
            <p className="text-xs mt-1">שנה את טווח התאריכים או הוסף פעולות</p>
          </div>
        ) : displayMode === 'chart' ? (
          /* ─── Composed Chart: balance area + income/expense bars ──── */
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 20, bottom: 5 }}
              style={{ cursor: 'pointer' }}
              onClick={(e: any) => {
                // Debug: confirm the click event fires
                console.log('[Chart click]', e);

                // Strategy: activePayload is only set when the cursor is
                // hovering near the line stroke.  Clicking the filled area
                // below the line often returns activePayload=null even though
                // the event fires correctly.
                //
                // Robust fix: prefer activePayload when available, otherwise
                // fall back to e.activeLabel (the x-axis key Recharts ALWAYS
                // sets for any click inside the chart area) and look up the
                // matching ChartPoint in chartData ourselves.
                const point: ChartPoint | undefined =
                  e?.activePayload?.[0]?.payload ??
                  (e?.activeLabel
                    ? chartData.find(p => p.date === e.activeLabel)
                    : undefined);

                console.log('[Chart click] resolved point:', point);
                if (point) openDrillDown(point, 'all');
              }}
            >
              <defs>
                <linearGradient id="balanceGradientPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="balanceGradientNeg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={v => ILS.format(v as number)}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                width={80}
              />
              <RechartsTooltip
                content={<ChartTooltipContent />}
                cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
              />
              <ReferenceLine y={0} stroke="#d1d5db" strokeDasharray="4 4" strokeWidth={1.5} />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#2563eb"
                strokeWidth={2.5}
                fill={
                  (chartData[chartData.length - 1]?.balance ?? 0) >= 0
                    ? 'url(#balanceGradientPos)'
                    : 'url(#balanceGradientNeg)'
                }
                dot={chartData.length <= 31}
                // activeDot: visual only — click is handled at AreaChart level below
                activeDot={{ r: 7, fill: '#2563eb', stroke: '#fff', strokeWidth: 2, cursor: 'pointer' }}
              />
              {/* Income bars — positive values, grow upward */}
              <Bar dataKey="income" name="הכנסות" fill="#10b981" fillOpacity={0.7} maxBarSize={24} radius={[2, 2, 0, 0]} />
              {/* Expense bars — negative values, grow downward */}
              <Bar dataKey="expense" name="הוצאות" fill="#ef4444" fillOpacity={0.7} maxBarSize={24} radius={[0, 0, 2, 2]} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          /* ─── Mini table view ───────────────────────────────────────── */
          <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500">תאריך</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-green-600">הכנסות</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-red-600">הוצאות</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-blue-600">יתרת תזרים</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {chartData.map((p, i) => (
                  <tr key={i} className={`hover:bg-gray-50 ${p.balance < 0 ? 'bg-red-50/40' : ''}`}>
                    <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{p.date}</td>

                    {/* Income cell — clickable */}
                    <td
                      className={`px-4 py-2 tabular-nums whitespace-nowrap font-medium ${
                        p.income > 0
                          ? 'text-green-600 cursor-pointer hover:underline hover:text-green-800 transition-colors'
                          : 'text-gray-300'
                      }`}
                      onClick={() => p.income > 0 && openDrillDown(p, 'income')}
                      title={p.income > 0 ? 'לחץ לפירוט הכנסות' : undefined}
                    >
                      {p.income > 0 ? ILS2.format(p.income) : '—'}
                    </td>

                    {/* Expense cell — clickable */}
                    <td
                      className={`px-4 py-2 tabular-nums whitespace-nowrap font-medium ${
                        p.expense < 0
                          ? 'text-red-600 cursor-pointer hover:underline hover:text-red-800 transition-colors'
                          : 'text-gray-300'
                      }`}
                      onClick={() => p.expense < 0 && openDrillDown(p, 'expense')}
                      title={p.expense < 0 ? 'לחץ לפירוט הוצאות' : undefined}
                    >
                      {p.expense < 0 ? ILS2.format(p.expense) : '—'}
                    </td>

                    {/* Balance cell — clickable */}
                    <td
                      className={`px-4 py-2 font-bold tabular-nums whitespace-nowrap cursor-pointer
                        hover:underline transition-colors ${
                        p.balance >= 0 ? 'text-emerald-700 hover:text-emerald-900' : 'text-red-700 hover:text-red-900'
                      }`}
                      onClick={() => openDrillDown(p, 'all')}
                      title="לחץ לפירוט כל התנועות"
                    >
                      {ILS2.format(p.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Chart legend + interactivity hint */}
        {!loading && chartData.length > 0 && displayMode === 'chart' && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-6 h-0.5 bg-blue-600 rounded" />
              יתרה מצטברת
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              הכנסות בתקופה
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              הוצאות בתקופה
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 border-t-2 border-dashed border-gray-400" />
              קו אפס
            </span>
            <span className="flex items-center gap-1.5 text-blue-500 font-medium">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
              לחץ על הגרף לפירוט תנועות
            </span>
          </div>
        )}
      </div>

      {/* Drill-down modal                                                    */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <CashFlowDrillDownModal
        open={drillTarget !== null}
        target={drillTarget}
        rows={drillRows}
        onClose={() => setDrillTarget(null)}
      />

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Section D — Category Summary (independent state)                    */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <CategorySummarySection />

      {/* Period summary footer */}
      {!loading && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl bg-gray-50 border border-gray-200 px-5 py-3 text-sm text-gray-600">
          <span className="font-semibold text-gray-700">סיכום תקופה:</span>
          <span>
            סה"כ הכנסות:
            <strong className="text-green-700 mr-1">{ILS2.format(periodIncome)}</strong>
          </span>
          <span>
            סה"כ הוצאות:
            <strong className="text-red-700 mr-1">{ILS2.format(periodExpenses)}</strong>
          </span>
          <span>
            נטו:
            <strong className={`mr-1 ${(periodIncome + periodExpenses) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {ILS2.format(periodIncome + periodExpenses)}
            </strong>
          </span>
          <span className="text-gray-400">·</span>
          <span>{periodRows.length} פעולות בתקופה</span>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* Hidden Management Report — rendered off-screen, triggered by print  */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div style={{ position: 'absolute', top: -9999, left: -9999, width: 1122, overflow: 'hidden' }}>
        <ManagementReportPrintView
          ref={printRef}
          {...printProps}
        />
      </div>

    </div>
  );
}
